import { isValidInviteCode } from '../systems/nameGenerator.js';
import { joinPartyAsClient, restoreLocalPlayerToSoloParty, getPlayerName, getInviteCode, getSelectedCharacter } from '../systems/partySystem.js';
import { playMenuNav, playMenuSelect } from '../systems/sounds.js';
import {
    UI_SIZES,
    UI_TEXT_SIZES,
    UI_COLORS,
    UI_Z_LAYERS
} from '../config/uiConfig.js';

/**
 * Join Party Scene
 * Dedicated scene for entering a party invite code
 */
export function initJoinPartyScene(k) {
    k.scene('joinParty', () => {
        // Save original party state before any join attempt
        const originalState = {
            name: getPlayerName(),
            inviteCode: getInviteCode(),
            character: getSelectedCharacter()
        };
        let joinCancelled = false;

        // Background
        k.add([
            k.rect(k.width(), k.height()),
            k.pos(0, 0),
            k.color(...UI_COLORS.BG_DARK),
            k.fixed(),
            k.z(0)
        ]);

        // Title
        k.add([
            k.text('Join Party', { size: UI_TEXT_SIZES.TITLE }),
            k.pos(k.width() / 2, 100),
            k.anchor('center'),
            k.color(...UI_COLORS.TEXT_PRIMARY),
            k.fixed(),
            k.z(10)
        ]);

        // Instruction
        k.add([
            k.text('Enter 6-digit invite code:', { size: UI_TEXT_SIZES.LABEL }),
            k.pos(k.width() / 2, 180),
            k.anchor('center'),
            k.color(...UI_COLORS.TEXT_SECONDARY),
            k.fixed(),
            k.z(10)
        ]);

        // Input display
        let inputCode = '';
        const inputDisplay = k.add([
            k.text('______', { size: UI_TEXT_SIZES.H1 }),
            k.pos(k.width() / 2, 280),
            k.anchor('center'),
            k.color(...UI_COLORS.GOLD),
            k.fixed(),
            k.z(10)
        ]);

        // Error/Status message
        const errorMsg = k.add([
            k.text('', { size: UI_TEXT_SIZES.SMALL }),
            k.pos(k.width() / 2, 360),
            k.anchor('center'),
            k.color(...UI_COLORS.ERROR),
            k.fixed(),
            k.z(10)
        ]);

        // Instructions text
        k.add([
            k.text('Press ESC or click Cancel to go back', { size: UI_TEXT_SIZES.TINY }),
            k.pos(k.width() / 2, k.height() - 100),
            k.anchor('center'),
            k.color(...UI_COLORS.TEXT_DISABLED),
            k.fixed(),
            k.z(10)
        ]);

        let isJoining = false;

        // Update display
        function updateDisplay() {
            let display = '';
            for (let i = 0; i < 6; i++) {
                if (i < inputCode.length) {
                    display += inputCode[i];
                } else {
                    display += '_';
                }
            }
            inputDisplay.text = display;
        }

        // Character input handler
        k.onCharInput((ch) => {
            if (isJoining) return;

            // Only accept alphanumeric characters
            if (/[a-zA-Z0-9]/.test(ch) && inputCode.length < 6) {
                playMenuNav();
                inputCode += ch.toUpperCase();
                updateDisplay();
                errorMsg.text = '';

                // Auto-submit when 6 characters entered
                if (inputCode.length === 6) {
                    k.wait(0.2, attemptJoin);
                }
            }
        });

        // Backspace handler
        k.onKeyPress('backspace', () => {
            if (isJoining) return;

            if (inputCode.length > 0) {
                playMenuNav();
                inputCode = inputCode.slice(0, -1);
                updateDisplay();
                errorMsg.text = '';
            }
        });

        // Escape key to cancel (works even during join attempt)
        k.onKeyPress('escape', () => {
            playMenuNav();
            joinCancelled = true;
            isJoining = false;
            // Restore original party state
            restoreLocalPlayerToSoloParty(originalState);
            k.go('menu');
        });

        // Enter key to submit
        k.onKeyPress('enter', () => {
            if (isJoining) return;
            if (inputCode.length === 6) {
                attemptJoin();
            }
        });

        // Animated "Joining party..." status (cycling dots) + client-side timeout
        let joiningAnim = null;   // k.loop handle for the dot animation
        let joinTimeout = null;   // k.wait handle for the client-side timeout

        function stopJoiningFeedback() {
            if (joiningAnim) {
                joiningAnim.cancel();
                joiningAnim = null;
            }
            if (joinTimeout) {
                joinTimeout.cancel();
                joinTimeout = null;
            }
        }

        // Attempt to join party
        async function attemptJoin() {
            if (isJoining) return;

            const code = inputCode.trim();

            if (!isValidInviteCode(code)) {
                errorMsg.text = 'Invalid invite code format';
                errorMsg.color = k.rgb(...UI_COLORS.ERROR);
                return;
            }

            isJoining = true;
            errorMsg.color = k.rgb(...UI_COLORS.GOLD);

            // Animate the status so the screen doesn't look frozen while input is locked
            let dotCount = 1;
            errorMsg.text = 'Joining party.';
            joiningAnim = k.loop(0.4, () => {
                dotCount = (dotCount % 3) + 1;
                errorMsg.text = 'Joining party' + '.'.repeat(dotCount);
            });

            // Client-side timeout in case the host is unreachable and the join never resolves
            joinTimeout = k.wait(12, () => {
                if (joinCancelled) return;
                stopJoiningFeedback();
                isJoining = false;
                errorMsg.text = 'Could not reach host — check the code and try again';
                errorMsg.color = k.rgb(...UI_COLORS.ERROR);
                inputCode = '';
                updateDisplay();
            });

            try {
                const success = await joinPartyAsClient(code);

                if (success) {
                    // Check if join was cancelled while waiting
                    if (joinCancelled) {
                        stopJoiningFeedback();
                        return;
                    }
                    stopJoiningFeedback();
                    playMenuSelect();
                    errorMsg.text = 'Successfully joined party!';
                    errorMsg.color = k.rgb(...UI_COLORS.SUCCESS);

                    // Wait a moment then go to menu (now in party)
                    k.wait(1, () => {
                        if (!joinCancelled) {
                            k.go('menu');
                        }
                    });
                } else {
                    stopJoiningFeedback();
                    isJoining = false;
                    errorMsg.text = 'Failed to join party. Check the code and try again.';
                    errorMsg.color = k.rgb(...UI_COLORS.ERROR);
                    inputCode = '';
                    updateDisplay();
                }
            } catch (error) {
                stopJoiningFeedback();
                isJoining = false;
                console.error('[JoinParty] Error joining party:', error);
                errorMsg.text = 'Connection error. Please try again.';
                errorMsg.color = k.rgb(...UI_COLORS.ERROR);
                inputCode = '';
                updateDisplay();
            }
        }

        // Action buttons (MD size) - primary JOIN beside secondary CANCEL
        const { MD } = UI_SIZES.BUTTON;
        const buttonY = k.height() - 180;
        const buttonGap = 20;
        const joinX = k.width() / 2 - (MD.width / 2 + buttonGap / 2);
        const cancelX = k.width() / 2 + (MD.width / 2 + buttonGap / 2);

        // Join button (MD size - primary action)
        const joinButton = k.add([
            k.rect(MD.width, MD.height),
            k.pos(joinX, buttonY),
            k.anchor('center'),
            k.color(...UI_COLORS.PRIMARY),
            k.outline(2, k.rgb(...UI_COLORS.BORDER)),
            k.area(),
            k.fixed(),
            k.z(10)
        ]);

        k.add([
            k.text('JOIN', { size: UI_TEXT_SIZES.BODY }),
            k.pos(joinX, buttonY),
            k.anchor('center'),
            k.color(...UI_COLORS.TEXT_PRIMARY),
            k.fixed(),
            k.z(11)
        ]);

        joinButton.onClick(() => {
            attemptJoin();
        });

        joinButton.onHoverUpdate(() => {
            if (!isJoining) {
                joinButton.color = k.rgb(...UI_COLORS.PRIMARY_HOVER);
            }
        });

        joinButton.onHoverEnd(() => {
            joinButton.color = k.rgb(...UI_COLORS.PRIMARY);
        });

        // Cancel button (MD size - secondary action)
        const cancelButton = k.add([
            k.rect(MD.width, MD.height),
            k.pos(cancelX, buttonY),
            k.anchor('center'),
            k.color(...UI_COLORS.NEUTRAL),
            k.outline(2, k.rgb(...UI_COLORS.BORDER)),
            k.area(),
            k.fixed(),
            k.z(10)
        ]);

        const cancelText = k.add([
            k.text('CANCEL', { size: UI_TEXT_SIZES.BODY }),
            k.pos(cancelX, buttonY),
            k.anchor('center'),
            k.color(...UI_COLORS.TEXT_SECONDARY),
            k.fixed(),
            k.z(11)
        ]);

        cancelButton.onClick(() => {
            playMenuNav();
            joinCancelled = true;
            isJoining = false;
            // Restore original party state
            restoreLocalPlayerToSoloParty(originalState);
            k.go('menu');
        });

        cancelButton.onHoverUpdate(() => {
            if (!isJoining) {
                cancelButton.color = k.rgb(...UI_COLORS.NEUTRAL_HOVER);
            }
        });

        cancelButton.onHoverEnd(() => {
            cancelButton.color = k.rgb(...UI_COLORS.NEUTRAL);
        });
    });
}
